import * as React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { cn } from '../../lib/utils';

export type DroppableState = 'default' | 'active' | 'correct' | 'incorrect';

export interface DroppableZoneProps {
    id: string;
    children: React.ReactNode;
    state?: DroppableState;
    disabled?: boolean;
    isSelected?: boolean;
    isDragActive?: boolean;
    onClick?: () => void;
    className?: string;
    style?: React.CSSProperties;
    /**
     * EAA-1680: which playful-skin pressable hook to stamp — 'option' for
     * row/card-sized drop targets (e.g. match destination cards), 'chip'
     * for small inline targets (e.g. FITB blanks). See
     * docs/PLAN-Playful-Interactive-Options.md Phase 4.
     */
    surface?: 'option' | 'chip';
}

export const DroppableZone = React.forwardRef<HTMLDivElement, DroppableZoneProps>(
    (
        {
            id,
            children,
            state = 'default',
            disabled = false,
            isSelected = false,
            isDragActive = false,
            onClick,
            className,
            style,
            surface = 'option',
        },
        ref,
    ) => {
        const { setNodeRef, isOver } = useDroppable({ id });

        const showDropHint = isDragActive && !disabled && state === 'default';
        const hookClass = surface === 'chip' ? 'content-chip' : 'content-option';

        const mergedRef = React.useCallback(
            (node: HTMLDivElement | null) => {
                setNodeRef(node);
                if (typeof ref === 'function') ref(node);
                else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
            },
            [setNodeRef, ref],
        );

        return (
            <div
                ref={mergedRef}
                className={cn(
                    'rounded-sm border bg-muted/30 p-2 relative',
                    'transition-[border-color,background-color,box-shadow,ring-color] duration-200 ease-out',
                    state === 'active' && 'border-2 border-primary',
                    state === 'correct' && 'border-2 border-success bg-success/10',
                    state === 'incorrect' && 'border-2 border-destructive bg-destructive/10 animate-shake',
                    isSelected && 'ring-2 ring-primary',
                    showDropHint && !isOver && 'border-dashed border-primary/40 bg-primary/[0.03]',
                    isOver && !disabled && 'ring-2 ring-primary bg-primary/10 border-primary shadow-sm',
                    disabled ? 'cursor-default' : 'cursor-pointer',
                    // EAA-1680: playful-skin pressable hooks (see DraggableItem
                    // for the paired dragging-surface half of this pattern).
                    hookClass,
                    disabled && `${hookClass}--static`,
                    isSelected && `${hookClass}--selected`,
                    state === 'correct' && 'content-feedback--correct',
                    state === 'incorrect' && 'content-feedback--incorrect',
                    className
                )}
                style={style}
                onClick={() => !disabled && onClick?.()}
            >
                {children}
            </div>
        );
    },
);

DroppableZone.displayName = 'DroppableZone';
